/**
 * Admin invitation management API.
 *
 * Wraps the backend /admin/registration-invitations endpoints.
 */
import { apiClient } from '@/services/api';
import type {
  RegistrationInvitation,
  InvitationListResponse,
  CreateInvitationPayload,
  InvitationStatus,
} from '@/types/admin';

// ─── Read ─────────────────────────────────────────────────────────────────────

export interface ListInvitationsParams {
  /** Filter by derived status.  'all' returns every record. */
  status?: InvitationStatus | 'all';
}

/**
 * Returns a paginated list of registration invitations.
 * The backend derives "active/used/expired" from the record fields.
 */
export async function listInvitations(
  params?: ListInvitationsParams
): Promise<InvitationListResponse> {
  const query: Record<string, string> = {};
  if (params?.status && params.status !== 'all') {
    query.status = params.status;
  }
  const response = await apiClient.get<InvitationListResponse>(
    '/admin/registration-invitations',
    { params: query }
  );
  return response.data;
}

export async function getInvitation(id: string): Promise<RegistrationInvitation> {
  const response = await apiClient.get<RegistrationInvitation>(
    `/admin/registration-invitations/${id}`
  );
  return response.data;
}

// ─── Create / Mutate ──────────────────────────────────────────────────────────

export async function createInvitation(
  payload: CreateInvitationPayload
): Promise<RegistrationInvitation> {
  const response = await apiClient.post<RegistrationInvitation>(
    '/admin/registration-invitations',
    payload
  );
  return response.data;
}

/**
 * Revokes an invitation by ID (sets it as used with no user).
 * The backend treats DELETE as revocation, not hard deletion.
 */
export async function revokeInvitation(id: string): Promise<void> {
  await apiClient.delete(`/admin/registration-invitations/${id}`);
}

/**
 * Generates a new token for the invitation and re-sends the email.
 */
export async function resendInvitation(id: string): Promise<RegistrationInvitation> {
  const response = await apiClient.post<RegistrationInvitation>(
    `/admin/registration-invitations/${id}/resend`
  );
  return response.data;
}
